// Fusion des blocs « Vérifications » et « Questions clés » (reprise front D9).
//
// Les deux répondaient au même besoin — armer le BM avant l'entretien — et étaient
// présentés comme deux listes sans lien. `objection_handling` donne les objections
// anticipées avec leur contre-argument, `verification_questions` les questions à poser.
// C'est la même conversation, dans le même ordre.
//
// MASQUÉ QUAND VIDE (arbitrage Axel du 2026-08-16). À savoir : le moteur ne génère ces
// deux champs que si `raw_llm_score >= 45`, donc le bloc est absent sur environ deux
// lignes sur trois — knockoutés et profils plafonnés compris. Ce seuil est une décision
// moteur, tracée pour après le benchmark.

import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, HelpCircle, ShieldCheck } from 'lucide-react';
import type { ObjectionItem, VerificationQuestion } from '../../lib/mappers/match.mapper';

interface Props {
    objections?: ObjectionItem[];
    questions?: (string | VerificationQuestion)[];
    /** Ouvert par défaut : contrairement au détail du score, c'est de l'opérationnel. */
    defaultOpen?: boolean;
}

const CandidateValidation: React.FC<Props> = ({ objections, questions, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);

    const obj = objections ?? [];
    const qs = questions ?? [];
    const total = obj.length + qs.length;
    if (total === 0) return null;

    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
            >
                <span className="flex items-center gap-1.5 min-w-0">
                    <ShieldCheck size={12} className="text-indigo-500 shrink-0" />
                    <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                        Valider le candidat
                    </span>
                    <span className="text-[10px] font-medium text-slate-400 truncate">
                        {obj.length > 0 && `${obj.length} point${obj.length > 1 ? 's' : ''} à lever`}
                        {obj.length > 0 && qs.length > 0 && ' · '}
                        {qs.length > 0 && `${qs.length} question${qs.length > 1 ? 's' : ''}`}
                    </span>
                </span>
                {open ? <ChevronUp size={14} className="text-slate-400 shrink-0" /> : <ChevronDown size={14} className="text-slate-400 shrink-0" />}
            </button>

            {/* Deux colonnes : les points à lever d'un côté, les questions qui les
                tranchent de l'autre. Sur mobile elles s'empilent. */}
            {open && (
                <div className="border-t border-slate-100 grid grid-cols-1 md:grid-cols-2">
                    {/* Les objections d'abord : ce sont elles qui décident si l'entretien
                        vaut la peine. Les questions servent à les trancher. */}
                    {obj.length > 0 && (
                        <div className="md:border-r md:border-slate-100">
                            <p className="px-3 py-1.5 text-[9px] font-bold text-amber-700 uppercase tracking-wider bg-amber-50 border-b border-amber-100">
                                Points à lever
                            </p>
                            <div className="divide-y divide-slate-50">
                                {obj.map((item, i) => (
                                    <div key={`o-${i}`} className="px-3 py-2.5">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle size={12} className="text-amber-500 mt-0.5 shrink-0" />
                                            <p className="text-xs font-semibold text-slate-800 leading-snug">{item.potential_objection}</p>
                                        </div>
                                        {item.counter_argument && (
                                            <p className="text-[11px] text-slate-600 leading-snug ml-[20px] mt-1">
                                                <span className="font-bold text-slate-400">Réponse </span>{item.counter_argument}
                                            </p>
                                        )}
                                        {item.expected_answer && (
                                            <p className="text-[11px] text-emerald-700 leading-snug ml-[20px] mt-0.5">
                                                <span className="font-bold">Attendu </span>{item.expected_answer}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {qs.length > 0 && (
                        <div>
                            <p className="px-3 py-1.5 text-[9px] font-bold text-indigo-700 uppercase tracking-wider bg-indigo-50 border-b border-indigo-100">
                                Questions de validation
                            </p>
                            <div className="divide-y divide-slate-50">
                                {qs.map((q, i) => {
                                    const structured = typeof q === 'object' && q !== null;
                                    const vq = q as VerificationQuestion;
                                    return (
                                        <div key={`q-${i}`} className="px-3 py-2.5">
                                            <div className="flex items-start gap-2">
                                                <HelpCircle size={12} className="text-indigo-400 mt-0.5 shrink-0" />
                                                <p className="text-xs font-semibold text-slate-800 leading-snug">
                                                    {structured ? vq.question : String(q)}
                                                </p>
                                            </div>
                                            {structured && vq.context && (
                                                <p className="text-[11px] text-slate-500 italic leading-snug ml-[20px] mt-1">{vq.context}</p>
                                            )}
                                            {structured && vq.expected_answer && (
                                                <p className="text-[11px] text-emerald-700 leading-snug ml-[20px] mt-0.5">
                                                    <span className="font-bold">Attendu </span>{vq.expected_answer}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CandidateValidation;
